import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/services";
import { queriesToRefresh } from "./integrationRefreshScope";
import type {
  CalendarConnectionRuntime,
  ConferencingRuntimeState,
  ProviderAwareStatusMap,
  ProviderCalendarSummary,
  ProviderCapabilityFlags,
  ProviderCapabilityMap,
} from "@/services/types";
import { getCurrentRelativeUrl } from "@/lib/authRedirect";
import { useAuth } from "@/state/AuthContext";
import { oauthDebug } from "@/lib/authDebug";
import { redirectToExternal } from "@/lib/redirectSafety";
import { opsLogger } from "@/lib/opsLogger";
import { flattenStatusMap, normalizeIntegrationUiStatus, parseCalendarRuntimeStatus, parseStatusEnvelope, readStatusString } from "@/domain/adapters/integrationStatusAdapter";
import { isOAuthConferencingProvider, toCanonicalProviderId } from "@/lib/providerIds";
import { beginGlobalActivity, waitForNextPaint } from "@/lib/networkActivity";

export type IntegrationKind = "calendar" | "conferencing";
export type IntegrationProviderId = string;
export type IntegrationUiStatus = "connected" | "disconnected" | "syncing" | "failed";
export type IntegrationToast = {
  id: number;
  tone: "success" | "danger";
  title: string;
  description?: string;
  exiting?: boolean;
};

const OAUTH_PENDING_KEY = "integration-oauth-pending";

interface StoredOAuthPending {
  provider?: string;
  kind?: string;
  returnTo?: string;
  startedAt?: number;
}

interface PendingAction {
  kind: IntegrationKind;
  provider: IntegrationProviderId;
  action: "connect" | "disconnect";
  /**
   * Set when the action targets one specific connected account. Without it, a user with two
   * Google accounts would see "Removing…" on both rows while only one is being removed.
   */
  connectionId?: string;
}

interface IntegrationStateValue {
  calendarStatus: ProviderAwareStatusMap;
  conferencingStatus: ProviderAwareStatusMap;
  calendarCapabilities: ProviderCapabilityMap;
  conferencingCapabilities: ProviderCapabilityMap;
  statusMap: Record<string, string>;
  calendarConnections: CalendarConnectionRuntime[];
  conferencingRuntime: ConferencingRuntimeState;
  loading: boolean;
  error: string | null;
  toasts: IntegrationToast[];
  pendingAction: PendingAction | null;
  isResolvingOAuthReturn: boolean;
  isManuallyRefreshing: boolean;
  refreshStatus: (kind?: IntegrationKind, provider?: IntegrationProviderId) => Promise<void>;
  manualRefreshStatus: () => Promise<void>;
  startConnect: (kind: IntegrationKind, provider: IntegrationProviderId, returnTo?: string) => Promise<void>;
  disconnectProvider: (kind: IntegrationKind, provider: IntegrationProviderId) => Promise<void>;
  disconnectCalendarConnection: (connectionId: string, provider: IntegrationProviderId, email?: string) => Promise<void>;
  setDefaultWritebackConnection: (connectionId: string, provider: IntegrationProviderId) => Promise<void>;
  setCalendarChecksAvailability: (connectionId: string, calendarId: string, checks: boolean) => Promise<void>;
  setWritebackCalendar: (connectionId: string, calendarId: string) => Promise<void>;
  setDefaultConferencing: (provider: "google_meet" | "microsoft_teams" | "zoom" | "none") => Promise<void>;
  startGoogleConnect: (returnTo?: string) => Promise<void>;
  disconnect: (provider: IntegrationProviderId) => Promise<void>;
  dismissToast: (id: number) => void;
  getProviderStatus: (provider: IntegrationProviderId) => IntegrationUiStatus;
  getCalendarProviderStatus: (provider: IntegrationProviderId) => IntegrationUiStatus;
  getConferencingProviderStatus: (provider: IntegrationProviderId) => IntegrationUiStatus;
  getProviderCalendars: (provider: IntegrationProviderId) => ProviderCalendarSummary[];
  getCalendarConnections: () => CalendarConnectionRuntime[];
  getCalendarCapability: (providerEnum: string) => ProviderCapabilityFlags | null;
  getConferencingCapability: (providerEnum: string) => ProviderCapabilityFlags | null;
  hasCalendarCapability: (providerEnum: string) => boolean;
  hasConferencingCapability: (providerEnum: string) => boolean;
}

const IntegrationContext = createContext<IntegrationStateValue | null>(null);

function buildCalendarStatusFromConnections(connections: CalendarConnectionRuntime[]): ProviderAwareStatusMap {
  const byProvider = new Map<string, string>();
  connections.forEach((connection) => {
    const provider = toCanonicalProviderId(connection.provider);
    const normalized = String(connection.status ?? "").toUpperCase();
    const next =
      normalized === "CONNECTED" ? "CONNECTED" :
      normalized.includes("SYNC") ? "SYNCING" :
      normalized.includes("ERROR") || normalized.includes("FAIL") ? "ERROR" :
      normalized || "DISCONNECTED";
    const current = byProvider.get(provider);
    if (!current) {
      byProvider.set(provider, next);
      return;
    }
    if (current === "CONNECTED") return;
    if (next === "CONNECTED") {
      byProvider.set(provider, next);
      return;
    }
    if (current === "SYNCING") return;
    if (next === "SYNCING") {
      byProvider.set(provider, next);
      return;
    }
    if (current === "ERROR") return;
    if (next === "ERROR") byProvider.set(provider, next);
  });
  const out: ProviderAwareStatusMap = {};
  byProvider.forEach((status, provider) => {
    out[provider] = { status };
  });
  return out;
}

function getOptimisticStatus(
  baseStatus: IntegrationUiStatus,
  pendingAction: PendingAction | null,
  isResolvingOAuthReturn: boolean,
  kind: IntegrationKind,
  provider: IntegrationProviderId,
): IntegrationUiStatus {
  if (pendingAction?.kind === kind && pendingAction.provider === provider) {
    return pendingAction.action === "disconnect" ? "disconnected" : baseStatus;
  }
  if (isResolvingOAuthReturn && pendingAction?.kind === kind && pendingAction.provider === provider && baseStatus === "disconnected") {
    return "syncing";
  }
  return baseStatus;
}

function isCalendarBackedConferencingProvider(provider?: IntegrationProviderId | null) {
  return provider === "google" || provider === "microsoft";
}

function readStoredOAuthPending(): StoredOAuthPending | null {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem(OAUTH_PENDING_KEY);
  if (!raw) return null;
  try {
    const pending = JSON.parse(raw) as StoredOAuthPending;
    const startedAt = typeof pending.startedAt === "number" ? pending.startedAt : 0;
    if (startedAt && Date.now() - startedAt > 10 * 60 * 1000) {
      sessionStorage.removeItem(OAUTH_PENDING_KEY);
      return null;
    }
    return pending;
  } catch {
    sessionStorage.removeItem(OAUTH_PENDING_KEY);
    return null;
  }
}

function clearStoredOAuthPending() {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(OAUTH_PENDING_KEY);
}

function shouldResumeOAuthForLocation(location: { pathname: string; search: string; hash: string }, pending: StoredOAuthPending | null) {
  if (!pending?.returnTo) return false;
  const current = `${location.pathname}${location.search}${location.hash}`;
  if (current === pending.returnTo) return true;
  const params = new URLSearchParams(location.search);
  return Boolean(params.get("integrationSuccess") || params.get("error"));
}

/**
 * What the writeback calendar can mint a meeting link for — the same rule the server applies when it
 * reports `googleMeetAvailable` / `teamsAvailable`: Meet only on a Google calendar, Teams only on a
 * Microsoft work/school account with Teams enabled. Used to move the conferencing card the instant
 * the writeback changes instead of waiting for the status refetch to say so.
 */
function conferencingCapabilityOf(connection: Record<string, unknown> | undefined) {
  const account = (connection?.account ?? {}) as { type?: unknown; supportsNativeTeams?: unknown };
  const provider = connection?.provider;
  return {
    googleMeetAvailable: provider === "google",
    teamsAvailable: provider === "microsoft"
      && String(account.type ?? "").toUpperCase() === "MICROSOFT_365"
      && account.supportsNativeTeams === true,
  };
}

export function IntegrationProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [toasts, setToasts] = useState<IntegrationToast[]>([]);
  const TOAST_EXIT_MS = 220;
  const showToast = useCallback((toast: Omit<IntegrationToast, "id">) => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { ...toast, id, exiting: false }]);
    window.setTimeout(() => {
      setToasts((current) => current.map((item) => (item.id === id ? { ...item, exiting: true } : item)));
      window.setTimeout(() => {
        setToasts((current) => current.filter((item) => item.id !== id));
      }, TOAST_EXIT_MS);
    }, 4200);
  }, []);
  const dismissToast = useCallback((id: number) => {
    setToasts((current) => current.map((item) => (item.id === id ? { ...item, exiting: true } : item)));
    window.setTimeout(() => {
      setToasts((current) => current.filter((item) => item.id !== id));
    }, TOAST_EXIT_MS);
  }, []);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(() => {
    const pending = readStoredOAuthPending();
    if (!shouldResumeOAuthForLocation(location, pending)) return null;
    const pendingProvider = typeof pending?.provider === "string" ? toCanonicalProviderId(pending.provider) : null;
    const pendingKind = pending?.kind === "conferencing" ? "conferencing" : pending?.kind === "calendar" ? "calendar" : null;
    return pendingProvider && pendingKind ? { provider: pendingProvider, kind: pendingKind, action: "connect" } : null;
  });
  const [isResolvingOAuthReturn, setIsResolvingOAuthReturn] = useState(() => shouldResumeOAuthForLocation(location, readStoredOAuthPending()));
  const [manualError, setManualError] = useState<string | null>(null);
  const [isManuallyRefreshing, setIsManuallyRefreshing] = useState(false);
  const oauthResolutionHandledRef = useRef(false);
  // Optimistic edits (availability toggle, writeback pick) write straight into the status cache and
  // the server catches up a round-trip later. A status fetch that was issued before an edit lands
  // reports the pre-edit state; applying it flips the UI back until the next refresh corrects it.
  // `epoch` ticks on every edit and `inFlight` counts edits the server has not acknowledged, so the
  // query below can tell a reply is stale and keep the optimistic cache instead.
  const optimisticEpochRef = useRef(0);
  const optimisticInFlightRef = useRef(0);

  const calendarQuery = useQuery({
    queryKey: ["calendar-status"],
    queryFn: async () => {
      const epoch = optimisticEpochRef.current;
      const fresh = await api.getCalendarStatus();
      if (optimisticEpochRef.current !== epoch || optimisticInFlightRef.current > 0) {
        const cached = queryClient.getQueryData<typeof fresh>(["calendar-status"]);
        if (cached !== undefined) return cached;
      }
      return fresh;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: Boolean(user?.id),
  });

  const conferencingQuery = useQuery({
    queryKey: ["conferencing-status"],
    queryFn: () => api.getConferencingStatus(),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: Boolean(user?.id),
  });

  const calendarData = calendarQuery.data;
  const conferencingData = conferencingQuery.data;

  const calendarParsed = useMemo(() => {
    if (!calendarData) {
      return {
        calendarStatus: {} as ProviderAwareStatusMap,
        calendarCapabilities: {} as ProviderCapabilityMap,
        calendarConnections: [] as CalendarConnectionRuntime[],
        conferencingRuntime: { zoomConnected: false, googleMeetAvailable: false, teamsAvailable: false, defaultProvider: "none" } satisfies ConferencingRuntimeState,
      };
    }

    const runtime = parseCalendarRuntimeStatus(calendarData);
    const parsed = parseStatusEnvelope(calendarData, "calendar");
    const derived = buildCalendarStatusFromConnections(runtime.connections);
    const calendarStatus = Object.keys(parsed.providers).length > 0
      ? { ...derived, ...parsed.providers }
      : derived;

    return {
      calendarStatus,
      calendarCapabilities: parsed.capabilities,
      calendarConnections: runtime.connections,
      conferencingRuntime: runtime.conferencing,
    };
  }, [calendarData]);

  const conferencingParsed = useMemo(() => {
    if (!conferencingData) {
      return {
        conferencingStatus: {} as ProviderAwareStatusMap,
        conferencingCapabilities: {} as ProviderCapabilityMap,
      };
    }

    const parsed = parseStatusEnvelope(conferencingData, "conferencing");
    return {
      conferencingStatus: parsed.providers,
      conferencingCapabilities: parsed.capabilities,
    };
  }, [conferencingData]);

  const loading = calendarQuery.isLoading || conferencingQuery.isLoading;
  const error = useMemo(() => {
    if (manualError) return manualError;
    if (calendarData || conferencingData) return null;
    const hasCalendarError = calendarQuery.isError;
    const hasConferencingError = conferencingQuery.isError;
    if (!hasCalendarError && !hasConferencingError) return null;
    return "Unable to load integration status.";
  }, [calendarData, calendarQuery.isError, conferencingData, conferencingQuery.isError, manualError]);

  useEffect(() => {
    if (!user?.id) return;
    if (calendarQuery.data && calendarQuery.isStale && !calendarQuery.isFetching) {
      void queryClient.invalidateQueries({ queryKey: ["calendar-status"] });
    }
    if (conferencingQuery.data && conferencingQuery.isStale && !conferencingQuery.isFetching) {
      void queryClient.invalidateQueries({ queryKey: ["conferencing-status"] });
    }
  }, [
    calendarQuery.data,
    calendarQuery.isFetching,
    calendarQuery.isStale,
    conferencingQuery.data,
    conferencingQuery.isFetching,
    conferencingQuery.isStale,
    location.hash,
    location.pathname,
    location.search,
    queryClient,
    user?.id,
  ]);

  const refreshStatus = useCallback(async (kind?: IntegrationKind, provider?: IntegrationProviderId) => {
    if (!user?.id) return;
    const keys = queriesToRefresh(kind, isCalendarBackedConferencingProvider(provider));
    await Promise.all(
      keys.map((queryKey) => queryClient.invalidateQueries({ queryKey: [queryKey] })),
    );
  }, [queryClient, user?.id]);

  const manualRefreshStatus = useCallback(async () => {
    setIsManuallyRefreshing(true);
    try {
      await refreshStatus();
      await waitForNextPaint();
    } finally {
      setIsManuallyRefreshing(false);
    }
  }, [refreshStatus]);

  const resolveOAuthRefreshTransaction = useCallback(async (
    kind: IntegrationKind,
    provider: IntegrationProviderId | undefined,
    onSuccess?: () => void,
  ) => {
    try {
      await refreshStatus(kind, provider);
      onSuccess?.();
    } finally {
      clearStoredOAuthPending();
      setPendingAction(null);
      setIsResolvingOAuthReturn(false);
      oauthResolutionHandledRef.current = false;
      await waitForNextPaint();
    }
  }, [refreshStatus]);

  const startConnect = useCallback(async (kind: IntegrationKind, provider: IntegrationProviderId, returnTo?: string) => {
    const canonicalProvider = toCanonicalProviderId(provider);
    if (kind === "conferencing" && !isOAuthConferencingProvider(canonicalProvider)) {
      setManualError("This conferencing provider does not support OAuth connection.");
      return;
    }
    const target = returnTo ?? getCurrentRelativeUrl();
    oauthResolutionHandledRef.current = false;
    setPendingAction({ kind, provider: canonicalProvider, action: "connect" });
    setManualError(null);
    const endGlobalActivity = beginGlobalActivity("immediate");
    try {
      sessionStorage.setItem(OAUTH_PENDING_KEY, JSON.stringify({ kind, provider: canonicalProvider, returnTo: target, startedAt: Date.now() }));
      const redirectUrl = await api.getIntegrationConnectRedirectUrl(kind, canonicalProvider, { source: "host-dashboard", returnTo: target });
      await waitForNextPaint();
      redirectToExternal(redirectUrl, api.baseUrl, "href");
    } catch (e) {
      endGlobalActivity();
      clearStoredOAuthPending();
      opsLogger.warn({
        category: kind === "conferencing" ? "conferencing_integration_failure" : "calendar_integration_failure",
        message: `Failed to start ${canonicalProvider} ${kind} connect`,
      });
      console.error(e);
      const msg = e instanceof Error ? e.message : "";
      setManualError(msg || `Failed to start ${canonicalProvider} connect.`);
      setPendingAction(null);
    }
  }, []);

  const startGoogleConnect = useCallback(async (returnTo?: string) => {
    await startConnect("calendar", "google", returnTo);
  }, [startConnect]);

  const disconnectProvider = useCallback(async (kind: IntegrationKind, provider: IntegrationProviderId) => {
    const canonicalProvider = toCanonicalProviderId(provider);
    setPendingAction({ kind, provider: canonicalProvider, action: "disconnect" });
    setManualError(null);
    try {
      if (kind === "conferencing") {
        await api.disconnectConferencing(canonicalProvider);
      } else {
        await api.disconnectCalendar(canonicalProvider);
      }
      await refreshStatus(kind, canonicalProvider);
      showToast({
        tone: "success",
        title: `${canonicalProvider[0].toUpperCase()}${canonicalProvider.slice(1)} disconnected`,
        description: kind === "calendar"
          ? "Calendar sync and booking mirroring are now disabled for that provider."
          : "New bookings will no longer create conferencing links from that provider.",
      });
    } catch (e) {
      opsLogger.warn({
        category: kind === "conferencing" ? "conferencing_integration_failure" : "calendar_integration_failure",
        message: "Failed to disconnect provider",
        details: { provider: canonicalProvider, kind },
      });
      console.error(e);
      const msg = e instanceof Error ? e.message : "";
      if (
        kind === "conferencing" &&
        (canonicalProvider === "google_meet" || canonicalProvider === "microsoft_teams") &&
        msg.toLowerCase().includes("disconnect")
      ) {
        setManualError("This conferencing provider is calendar-backed. Disconnect its calendar provider instead.");
        showToast({
          tone: "danger",
          title: "Disconnect failed",
          description: "This conferencing provider is calendar-backed. Disconnect its calendar provider instead.",
        });
      } else {
        setManualError(msg || `Failed to disconnect ${canonicalProvider}.`);
        showToast({
          tone: "danger",
          title: "Disconnect failed",
          description: msg || `Failed to disconnect ${canonicalProvider}.`,
        });
      }
    } finally {
      setPendingAction(null);
    }
  }, [refreshStatus, showToast]);

  /**
   * Disconnects one connected calendar account. Distinct from disconnectProvider, which removes
   * every account for a provider — the wrong thing once a user has connected more than one.
   */
  const disconnectCalendarConnection = useCallback(async (
    connectionId: string,
    provider: IntegrationProviderId,
    email?: string,
  ) => {
    const canonicalProvider = toCanonicalProviderId(provider);
    setPendingAction({ kind: "calendar", provider: canonicalProvider, action: "disconnect", connectionId });
    setManualError(null);
    try {
      await api.disconnectCalendarConnection(connectionId);
      await refreshStatus("calendar", canonicalProvider);
      showToast({
        tone: "success",
        title: email ? `${email} disconnected` : "Calendar disconnected",
        description: "Its events no longer block your availability, and bookings will not be added to it.",
      });
    } catch (e) {
      opsLogger.warn({
        category: "calendar_integration_failure",
        message: "Failed to disconnect calendar connection",
        details: { provider: canonicalProvider, connectionId },
      });
      console.error(e);
      const msg = e instanceof Error ? e.message : "";
      setManualError(msg || "Failed to disconnect that calendar.");
      showToast({
        tone: "danger",
        title: "Disconnect failed",
        description: msg || "Failed to disconnect that calendar.",
      });
    } finally {
      setPendingAction(null);
    }
  }, [refreshStatus, showToast]);

  /**
   * Re-points round-robin booking write-back at a specific connected account.
   *
   * <p>Applied optimistically: picking a radio is a cheap, reversible preference, so the selection
   * moves the moment you click and the request goes out behind it. Waiting on the round-trip (and
   * blocking the page on a global loader) made a one-click toggle feel like a page load. If the
   * server rejects it we put the previous selection back and say so, rather than leaving the UI
   * asserting something that never happened.
   */
  const setDefaultWritebackConnection = useCallback(async (
    connectionId: string,
    provider: IntegrationProviderId,
  ) => {
    const canonicalProvider = toCanonicalProviderId(provider);
    setManualError(null);

    // The cached payload is the raw runtime envelope; CalendarStatusMap does not describe it
    // (the adapter parses it defensively from `unknown`), so narrow to just what we rewrite.
    type CachedCalendarStatus = { connections?: Array<{ connectionId?: string } & Record<string, unknown>> };

    // Move the flag onto `targetId`, clearing it everywhere else. Exactly one connection may carry
    // it (a partial unique index enforces that server-side), so selecting one clears the rest.
    const applySelection = (targetId: string | undefined, restoreProvider?: string) => {
      queryClient.setQueryData(["calendar-status"], (current: unknown) => {
        const envelope = current as (CachedCalendarStatus & { conferencing?: Record<string, unknown> }) | undefined;
        if (!envelope || !Array.isArray(envelope.connections)) return current;
        // The conferencing card hangs off the writeback calendar: move its availability with it, and
        // stand a native Meet/Teams default down when the new writeback cannot serve it (the server
        // does the same), so that card is in step with this one rather than a refetch behind.
        const caps = conferencingCapabilityOf(envelope.connections.find((c) => c.connectionId === targetId));
        const currentProvider = String(envelope.conferencing?.defaultProvider ?? "none");
        const stillServable = (currentProvider !== "google_meet" || caps.googleMeetAvailable)
          && (currentProvider !== "microsoft_teams" || caps.teamsAvailable);
        return {
          ...envelope,
          connections: envelope.connections.map((c) => ({
            ...c,
            defaultWriteback: c.connectionId === targetId,
          })),
          conferencing: {
            ...(envelope.conferencing ?? {}),
            googleMeetAvailable: caps.googleMeetAvailable,
            teamsAvailable: caps.teamsAvailable,
            defaultProvider: restoreProvider ?? (stillServable ? currentProvider : "none"),
          },
        };
      });
    };

    // Remember which connection held the flag so a failure can put it back. Read it from the
    // parsed connections rather than the query cache: this query uses placeholderData, and
    // placeholder data is never written to the cache, so getQueryData can legitimately be
    // undefined here — restoring that would silently no-op.
    const previouslySelectedId = calendarParsed.calendarConnections
      .find((c) => c.defaultWriteback)?.connectionId;

    const previousConferencingProvider = calendarParsed.conferencingRuntime.defaultProvider;
    applySelection(connectionId);
    optimisticEpochRef.current += 1;
    optimisticInFlightRef.current += 1;

    try {
      await api.setDefaultWritebackConnection(connectionId);
      optimisticInFlightRef.current -= 1;
      // Re-sync in the background so the cache reflects whatever the server actually stored.
      void refreshStatus("calendar", canonicalProvider);
    } catch (e) {
      optimisticInFlightRef.current -= 1;
      applySelection(previouslySelectedId, previousConferencingProvider);
      opsLogger.warn({
        category: "calendar_integration_failure",
        message: "Failed to set default writeback connection",
        details: { provider: canonicalProvider, connectionId },
      });
      console.error(e);
      const msg = e instanceof Error ? e.message : "";
      showToast({
        tone: "danger",
        title: "Could not change the writeback calendar",
        description: msg || "Please try again.",
      });
    }
  }, [calendarParsed.calendarConnections, calendarParsed.conferencingRuntime.defaultProvider, queryClient, refreshStatus, showToast]);

  // The cached payload is the raw runtime envelope; its per-calendar shape is not described by any
  // named type (the adapter parses it defensively from `unknown`), so narrow to just what we rewrite.
  type CachedCalendar = { calendarId?: string } & Record<string, unknown>;
  type CachedConnectionWithCalendars = {
    connectionId?: string;
    defaultWriteback?: boolean;
    calendars?: CachedCalendar[];
  } & Record<string, unknown>;
  type CachedCalendarStatusEnvelope = {
    connections?: CachedConnectionWithCalendars[];
    conferencing?: Record<string, unknown>;
  };

  /**
   * Toggles whether one calendar is checked for booking conflicts. Applied optimistically: it is a
   * visible on/off switch, so the flip has to land the instant it is clicked. On rejection we put the
   * previous value back and surface the backend's message rather than leave the switch lying.
   */
  const availabilityQueueRef = useRef<Promise<void>>(Promise.resolve());
  const availabilityInFlightRef = useRef(0);
  const setCalendarChecksAvailability = useCallback(async (
    connectionId: string,
    calendarId: string,
    checks: boolean,
  ) => {
    setManualError(null);
    const previousConnection = calendarParsed.calendarConnections
      .find((connection) => connection.connectionId === connectionId);
    const previousCalendar = previousConnection?.calendars
      .find((calendar) => calendar.calendarId === calendarId);
    const previousAvailability = previousCalendar?.selectedForAvailability ?? !checks;
    const previousProjection = previousCalendar?.selectedForProjection ?? false;
    const previousDefaultWriteback = previousConnection?.defaultWriteback ?? false;
    const previousConferencing = calendarParsed.conferencingRuntime.defaultProvider;
    const nativeConferencing = previousConferencing === "google_meet"
      || previousConferencing === "microsoft_teams";

    // Apply the full dependency chain in one cache update. Turning availability off removes the
    // same calendar from writeback; when that connection was the active writeback, its native
    // conferencing choice also stands down. Zoom is independent and remains selected.
    const previousMeetAvailable = calendarParsed.conferencingRuntime.googleMeetAvailable;
    const previousTeamsAvailable = calendarParsed.conferencingRuntime.teamsAvailable;
    const applyState = (
      availability: boolean,
      projection: boolean,
      defaultWriteback: boolean,
      conferencingProvider: string,
      meetAvailable: boolean,
      teamsAvailable: boolean,
    ) => {
      queryClient.setQueryData(["calendar-status"], (current: unknown) => {
        const envelope = current as CachedCalendarStatusEnvelope | undefined;
        if (!envelope || !Array.isArray(envelope.connections)) return current;
        return {
          ...envelope,
          connections: envelope.connections.map((connection) => {
            if (connection.connectionId !== connectionId || !Array.isArray(connection.calendars)) {
              return connection;
            }
            return {
              ...connection,
              defaultWriteback,
              calendars: connection.calendars.map((calendar) => calendar.calendarId === calendarId
                ? {
                    ...calendar,
                    selectedForAvailability: availability,
                    selectedForProjection: projection,
                  }
                : calendar),
            };
          }),
          conferencing: {
            ...(envelope.conferencing ?? {}),
            defaultProvider: conferencingProvider,
            googleMeetAvailable: meetAvailable,
            teamsAvailable: teamsAvailable,
          },
        };
      });
    };

    const cascadesWriteback = !checks && previousDefaultWriteback;
    applyState(
      checks,
      checks ? previousProjection : false,
      cascadesWriteback ? false : previousDefaultWriteback,
      cascadesWriteback && nativeConferencing ? "none" : previousConferencing,
      cascadesWriteback ? false : previousMeetAvailable,
      cascadesWriteback ? false : previousTeamsAvailable,
    );
    // Toggling quickly (off, then on) puts two requests in flight. Left alone they race: the first
    // request's status refresh can land after the second optimistic flip and repaint the OLD state,
    // so writeback/conferencing flicker available -> unavailable -> available. Requests therefore
    // run one at a time in click order, and only the last one reconciles with the server.
    availabilityInFlightRef.current += 1;
    optimisticEpochRef.current += 1;
    optimisticInFlightRef.current += 1;
    const run = async () => {
      let failed: unknown = null;
      try {
        await api.setCalendarChecksAvailability(connectionId, calendarId, checks);
      } catch (e) {
        failed = e;
      }
      availabilityInFlightRef.current -= 1;
      optimisticInFlightRef.current -= 1;
      const isLast = availabilityInFlightRef.current === 0;

      if (failed) {
        // A queued click supersedes this one: reverting now would clobber its optimistic state,
        // and the final refresh below settles on whatever the server actually stored.
        if (isLast) {
          applyState(
            previousAvailability,
            previousProjection,
            previousDefaultWriteback,
            previousConferencing,
            previousMeetAvailable,
            previousTeamsAvailable,
          );
        }
        opsLogger.warn({
          category: "calendar_integration_failure",
          message: "Failed to set calendar availability checking",
          details: { connectionId, calendarId, checks },
        });
        console.error(failed);
        const msg = failed instanceof Error ? failed.message : "";
        showToast({
          tone: "danger",
          title: "Could not change conflict checking",
          description: msg || "Please try again.",
        });
      }
      if (!isLast) return;
      // Availability calendar and holiday surfaces are projections of the same policy. Do not
      // leave their previous answers visible for the normal stale-time window after a successful
      // toggle; that makes a committed setting look ineffective.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["calendar-events"] }),
        queryClient.invalidateQueries({ queryKey: ["calendar-holidays"] }),
        refreshStatus(),
      ]).catch(() => undefined);
    };
    const queued = availabilityQueueRef.current.then(run, run);
    availabilityQueueRef.current = queued;
    await queued;
  }, [calendarParsed.calendarConnections, calendarParsed.conferencingRuntime.defaultProvider, calendarParsed.conferencingRuntime.googleMeetAvailable, calendarParsed.conferencingRuntime.teamsAvailable, queryClient, refreshStatus, showToast]);

  /**
   * Picks which calendar within a connection receives bookings. Optimistically sets projection on the
   * chosen calendar and clears it on its siblings in the same connection. Reverts + toasts on failure.
   */
  const setWritebackCalendar = useCallback(async (
    connectionId: string,
    calendarId: string,
  ) => {
    setManualError(null);
    const previouslySelectedId = calendarParsed.calendarConnections
      .find((c) => c.connectionId === connectionId)?.calendars
      .find((cal) => cal.selectedForProjection)?.calendarId;

    const applySelection = (targetId: string | undefined) => {
      queryClient.setQueryData(["calendar-status"], (current: unknown) => {
        const envelope = current as CachedCalendarStatusEnvelope | undefined;
        if (!envelope || !Array.isArray(envelope.connections)) return current;
        return {
          ...envelope,
          connections: envelope.connections.map((connection) => {
            if (connection.connectionId !== connectionId || !Array.isArray(connection.calendars)) {
              return connection;
            }
            return {
              ...connection,
              calendars: connection.calendars.map((calendar) => ({
                ...calendar,
                selectedForProjection: calendar.calendarId === targetId,
              })),
            };
          }),
        };
      });
    };

    applySelection(calendarId);
    try {
      await api.setWritebackCalendar(connectionId, calendarId);
      void refreshStatus("calendar");
    } catch (e) {
      applySelection(previouslySelectedId);
      opsLogger.warn({
        category: "calendar_integration_failure",
        message: "Failed to set writeback calendar",
        details: { connectionId, calendarId },
      });
      console.error(e);
      const msg = e instanceof Error ? e.message : "";
      showToast({
        tone: "danger",
        title: "Could not change the booking calendar",
        description: msg || "Please try again.",
      });
    }
  }, [calendarParsed.calendarConnections, queryClient, refreshStatus, showToast]);

  /**
   * Sets the user's default meeting link. Optimistic — but the whole point of the mutation is the
   * guardrail the backend enforces (a Meet link needs a Google write-back calendar, Teams a Microsoft
   * one). When it rejects, that explanatory message IS the feature: revert the selection and surface
   * it verbatim, never a generic "please try again".
   */
  const setDefaultConferencing = useCallback(async (
    provider: "google_meet" | "microsoft_teams" | "zoom" | "none",
  ) => {
    setManualError(null);
    type CachedEnvelopeWithConferencing = { conferencing?: Record<string, unknown> } & Record<string, unknown>;
    const previousProvider = calendarParsed.conferencingRuntime.defaultProvider;

    const applyProvider = (value: string) => {
      queryClient.setQueryData(["calendar-status"], (current: unknown) => {
        const envelope = current as CachedEnvelopeWithConferencing | undefined;
        if (!envelope || typeof envelope !== "object") return current;
        const conferencing = (envelope.conferencing && typeof envelope.conferencing === "object")
          ? envelope.conferencing
          : {};
        return {
          ...envelope,
          conferencing: { ...conferencing, defaultProvider: value },
        };
      });
    };

    applyProvider(provider);
    optimisticEpochRef.current += 1;
    optimisticInFlightRef.current += 1;
    try {
      await api.setDefaultConferencingProvider(provider);
      optimisticInFlightRef.current -= 1;
      void refreshStatus("calendar");
    } catch (e) {
      optimisticInFlightRef.current -= 1;
      applyProvider(previousProvider);
      opsLogger.warn({
        category: "conferencing_integration_failure",
        message: "Failed to set default conferencing provider",
        details: { provider },
      });
      console.error(e);
      const msg = e instanceof Error ? e.message : "";
      showToast({
        tone: "danger",
        title: "Could not change your default meeting link",
        description: msg || "Please try again.",
      });
    }
  }, [calendarParsed.conferencingRuntime.defaultProvider, queryClient, refreshStatus, showToast]);

  const disconnect = useCallback(async (provider: IntegrationProviderId) => {
    const kind: IntegrationKind = conferencingParsed.conferencingStatus[provider] ? "conferencing" : "calendar";
    await disconnectProvider(kind, provider);
  }, [conferencingParsed.conferencingStatus, disconnectProvider]);

  useEffect(() => {
    if (!user?.id) return;
    const pending = readStoredOAuthPending();
    if (!pending?.returnTo) return;
    const pendingProvider = typeof pending.provider === "string" ? toCanonicalProviderId(pending.provider) : null;
    const pendingKind = pending.kind === "conferencing" ? "conferencing" : pending.kind === "calendar" ? "calendar" : null;
    if (pendingProvider && pendingKind) {
      setPendingAction({ provider: pendingProvider, kind: pendingKind, action: "connect" });
    }
    const params = new URLSearchParams(location.search);
    if (params.get("integrationSuccess") || params.get("error")) {
      return;
    }
    const current = `${location.pathname}${location.search}${location.hash}`;
    if (current === pending.returnTo) {
      if (oauthResolutionHandledRef.current) return;
      oauthResolutionHandledRef.current = true;
      setIsResolvingOAuthReturn(true);
      clearStoredOAuthPending();
      const refreshKind: IntegrationKind = pendingKind ?? (pendingProvider && isOAuthConferencingProvider(pendingProvider) ? "conferencing" : "calendar");
      void resolveOAuthRefreshTransaction(refreshKind, pendingProvider ?? undefined, () => {
        showToast({
          tone: "success",
          title: "Integration updated",
          description: "Your provider status has been refreshed.",
        });
      });
      return;
    }
    const safeTarget = pending.returnTo.startsWith("/") ? pending.returnTo : `/${pending.returnTo}`;
    navigate(safeTarget, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate, resolveOAuthRefreshTransaction, showToast, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const params = new URLSearchParams(location.search);
    const integrationSuccess = params.get("integrationSuccess");
    const integrationError = params.get("error");
    const integrationErrorCode = params.get("code");
    if (!integrationSuccess && !integrationError) return;
    if (integrationError) {
      const detail = integrationErrorCode ? `${integrationError} (${integrationErrorCode})` : integrationError;
      setManualError(detail);
      clearStoredOAuthPending();
      setPendingAction(null);
      setIsResolvingOAuthReturn(false);
      oauthResolutionHandledRef.current = false;
      params.delete("error");
      params.delete("code");
    }
    if (!integrationSuccess) {
      const nextSearch = params.toString();
      const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}${location.hash}`;
      window.history.replaceState({}, "", nextUrl);
      return;
    }
    oauthDebug("integration success query detected", {
      provider: integrationSuccess,
      pathname: location.pathname,
    });
    clearStoredOAuthPending();
    if (oauthResolutionHandledRef.current) return;
    oauthResolutionHandledRef.current = true;
    setIsResolvingOAuthReturn(true);
    const refreshKind: IntegrationKind = isOAuthConferencingProvider(integrationSuccess) ? "conferencing" : "calendar";
    const canonicalSuccessProvider = toCanonicalProviderId(integrationSuccess);
    void resolveOAuthRefreshTransaction(refreshKind, canonicalSuccessProvider, () => {
        showToast({
          tone: "success",
          title: `${integrationSuccess} connected`,
          description: "The provider is now connected and ready to use.",
        });
        clearStoredOAuthPending();
        params.delete("integrationSuccess");
        const nextSearch = params.toString();
        const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}${location.hash}`;
        const callbackUrl = `${location.pathname}${location.search}${location.hash}`;
        if (getCurrentRelativeUrl() === callbackUrl) {
          window.history.replaceState({}, "", nextUrl);
          oauthDebug("cleaned integration success query param", { nextUrl });
        }
      });
  }, [location.hash, location.pathname, location.search, resolveOAuthRefreshTransaction, showToast, user?.id]);

  const statusMap = useMemo(() => flattenStatusMap(calendarParsed.calendarStatus, conferencingParsed.conferencingStatus), [calendarParsed.calendarStatus, conferencingParsed.conferencingStatus]);

  const getCalendarProviderStatusFn = useCallback((provider: IntegrationProviderId) => {
    const canonicalProvider = toCanonicalProviderId(provider);
    const baseStatus = normalizeIntegrationUiStatus(readStatusString(calendarParsed.calendarStatus[canonicalProvider]));
    return getOptimisticStatus(baseStatus, pendingAction, isResolvingOAuthReturn, "calendar", canonicalProvider);
  }, [calendarParsed.calendarStatus, isResolvingOAuthReturn, pendingAction]);

  const getConferencingProviderStatusFn = useCallback((provider: IntegrationProviderId) => {
    const canonicalProvider = toCanonicalProviderId(provider);
    const baseStatus = normalizeIntegrationUiStatus(readStatusString(conferencingParsed.conferencingStatus[canonicalProvider]));
    return getOptimisticStatus(baseStatus, pendingAction, isResolvingOAuthReturn, "conferencing", canonicalProvider);
  }, [conferencingParsed.conferencingStatus, isResolvingOAuthReturn, pendingAction]);

  const getProviderStatus = useCallback((provider: IntegrationProviderId) => {
    const calendar = getCalendarProviderStatusFn(provider);
    if (calendar !== "disconnected") return calendar;
    return getConferencingProviderStatusFn(provider);
  }, [getCalendarProviderStatusFn, getConferencingProviderStatusFn]);

  const getProviderCalendars = useCallback((provider: IntegrationProviderId) => {
    const entry = calendarParsed.calendarStatus[provider];
    if (!entry || typeof entry !== "object") return [];
    const list = Array.isArray(entry.calendars) ? entry.calendars : [];
    return list;
  }, [calendarParsed.calendarStatus]);

  const getCalendarConnections = useCallback(() => calendarParsed.calendarConnections, [calendarParsed.calendarConnections]);

  const getCalendarCapability = useCallback((providerEnum: string) => {
    return calendarParsed.calendarCapabilities[providerEnum.toUpperCase()] ?? null;
  }, [calendarParsed.calendarCapabilities]);

  const getConferencingCapability = useCallback((providerEnum: string) => {
    return conferencingParsed.conferencingCapabilities[providerEnum.toUpperCase()] ?? null;
  }, [conferencingParsed.conferencingCapabilities]);

  const hasCalendarCapability = useCallback((providerEnum: string) => {
    return Object.prototype.hasOwnProperty.call(calendarParsed.calendarCapabilities, providerEnum.toUpperCase());
  }, [calendarParsed.calendarCapabilities]);

  const hasConferencingCapability = useCallback((providerEnum: string) => {
    return Object.prototype.hasOwnProperty.call(conferencingParsed.conferencingCapabilities, providerEnum.toUpperCase());
  }, [conferencingParsed.conferencingCapabilities]);

  const value = useMemo<IntegrationStateValue>(() => ({
    calendarStatus: calendarParsed.calendarStatus,
    conferencingStatus: conferencingParsed.conferencingStatus,
    calendarCapabilities: calendarParsed.calendarCapabilities,
    conferencingCapabilities: conferencingParsed.conferencingCapabilities,
    calendarConnections: calendarParsed.calendarConnections,
    conferencingRuntime: calendarParsed.conferencingRuntime,
    statusMap,
    loading,
    error,
    toasts,
    pendingAction,
    isResolvingOAuthReturn,
    isManuallyRefreshing,
    refreshStatus,
    manualRefreshStatus,
    startConnect,
    disconnectProvider,
    disconnectCalendarConnection,
    setDefaultWritebackConnection,
    setCalendarChecksAvailability,
    setWritebackCalendar,
    setDefaultConferencing,
    startGoogleConnect,
    disconnect,
    dismissToast,
    getProviderStatus,
    getCalendarProviderStatus: getCalendarProviderStatusFn,
    getConferencingProviderStatus: getConferencingProviderStatusFn,
    getProviderCalendars,
    getCalendarConnections,
    getCalendarCapability,
    getConferencingCapability,
    hasCalendarCapability,
    hasConferencingCapability,
  }), [calendarParsed, conferencingParsed, disconnect, disconnectCalendarConnection, disconnectProvider, dismissToast, error, getCalendarCapability, getCalendarConnections, getCalendarProviderStatusFn, getConferencingCapability, getConferencingProviderStatusFn, getProviderCalendars, getProviderStatus, hasCalendarCapability, hasConferencingCapability, isManuallyRefreshing, isResolvingOAuthReturn, loading, manualRefreshStatus, pendingAction, refreshStatus, setCalendarChecksAvailability, setDefaultConferencing, setDefaultWritebackConnection, setWritebackCalendar, startConnect, startGoogleConnect, statusMap, toasts]);

  return <IntegrationContext.Provider value={value}>{children}</IntegrationContext.Provider>;
}

export function useIntegrationState() {
  const ctx = useContext(IntegrationContext);
  if (!ctx) throw new Error("useIntegrationState must be used within IntegrationProvider");
  return ctx;
}
